import { Button } from '@orbita-ui/core';
import { CuentaSelector } from 'core/cuenta/components/CuentaSelector/CuentaSelector';
import { TransactionListContext } from 'core/movimientos/contexts/TransactionListContext';
import { FiltrosMovimientos } from 'core/movimientos/models/FiltrosMovimientos';
import {
  ModoConsulta,
  SearchMovimientosRequest,
} from 'core/movimientos/models/SearchMovimientosRequest';
import { useCoreService } from 'core/services/useCoreService';
import { TarjetaSelector } from 'core/tarjeta/components/TarjetaSelector/TarjetaSelector';
import { useContext, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { FormWithValidation } from 'shared/form';
import { useDataFetcher } from 'shared/http';
import { Periodo } from 'shared/models/Periodo';
import { InputPlaceholder } from 'shared/placeholders';

interface FilterTransactionFormValue {
  periodo: Periodo;
  buscarPor: ModoConsulta;
  numeroCuenta: number;
  idTarjeta: number;
  nombreEstablecimiento: string;
  codigoRubro: string;
  detalleTipoOperacion: string;
}

const BUSCAR_POR_SELECT_OPTIONS = ['Cuenta', 'Tarjeta'];

const EMPTY_FORM_VALUE: FilterTransactionFormValue = {
  periodo: null,
  buscarPor: 'CUENTA' as ModoConsulta,
  numeroCuenta: null,
  idTarjeta: null,
  nombreEstablecimiento: null,
  codigoRubro: null,
  detalleTipoOperacion: null,
};

const EMPTY_FILTROS: FiltrosMovimientos = {
  establecimientos: [],
  rubros: [],
  detalleTipoOperacion: [],
};

const mapFormData = (formData: FilterTransactionFormValue): SearchMovimientosRequest => {
  return {
    mes: formData.periodo?.mes,
    anio: formData.periodo?.anio,
    buscarPor: formData.buscarPor,
    numeroCuenta: formData.numeroCuenta,
    idTarjeta: formData.idTarjeta,
    codigoRubro: formData.codigoRubro,
    detalleTipoOperacion: formData.detalleTipoOperacion,
    nombreEstablecimiento: formData.nombreEstablecimiento,
  };
};

export const FilterTransactionForm = () => {
  const { searchParams, reloadToken, searchTransactions } =
    useContext(TransactionListContext);
  const methods = useForm<FilterTransactionFormValue>({
    defaultValues: EMPTY_FORM_VALUE,
  });
  const coreService = useCoreService();

  const [filtrosMovimientos, setFiltrosMovimientosRequest] =
    useDataFetcher<FiltrosMovimientos>(EMPTY_FILTROS);

  const periodo = useWatch({ control: methods.control, name: 'periodo' });
  const buscarPor = useWatch({ control: methods.control, name: 'buscarPor' });
  const numeroCuenta = useWatch({ control: methods.control, name: 'numeroCuenta' });
  const idTarjeta = useWatch({ control: methods.control, name: 'idTarjeta' });

  useEffect(() => {
    setFiltrosMovimientosRequest(() =>
      coreService.getFiltrosMovimientos({
        anio: periodo?.anio,
        mes: periodo?.mes,
        buscarPor: buscarPor,
        idTarjeta: idTarjeta,
        numeroCuenta: numeroCuenta,
      }),
    );
  }, [
    buscarPor,
    coreService,
    idTarjeta,
    numeroCuenta,
    periodo?.anio,
    periodo?.mes,
    setFiltrosMovimientosRequest,
  ]);

  useEffect(() => {
    if (searchParams) {
      // reseteamos el formulario con los valores que vienen de la URL
      methods.reset({
        periodo: new Periodo(searchParams.mes, searchParams.anio),
        buscarPor: searchParams.buscarPor,
        numeroCuenta: searchParams.numeroCuenta,
        idTarjeta: searchParams.idTarjeta,
        nombreEstablecimiento: searchParams.nombreEstablecimiento,
        codigoRubro: searchParams.codigoRubro,
        detalleTipoOperacion: searchParams.detalleTipoOperacion,
      });
    }
  }, [searchParams, methods]);

  const handleSearch = (data: FilterTransactionFormValue) => {
    searchTransactions(mapFormData(data));
  };
  return (
    <FormWithValidation
      reactFormHookMethods={methods}
      onSubmit={handleSearch}
      className="m-3"
    >
      <Row>
        <Col>
          <FormWithValidation.LegacySelect.Period
            name="periodo"
            label="Periodo"
            placeholder="Seleccione un período"
          />
        </Col>
        <Col>
          <FormWithValidation.LegacySelect
            name="buscarPor"
            label="Buscar Por"
            options={BUSCAR_POR_SELECT_OPTIONS}
            mapOptions={true}
          />
        </Col>
        <Col>
          {buscarPor === 'CUENTA' ? (
            <CuentaSelector
              name="numeroCuenta"
              label="Cuenta"
              filtros={{
                cuentasHuerfanas: false,
                periodo: periodo,
              }}
              reloadToken={reloadToken}
            />
          ) : (
            <TarjetaSelector
              name="idTarjeta"
              filtros={{
                cuentasHuerfanas: false,
                periodo: periodo,
              }}
              reloadToken={reloadToken}
            />
          )}
        </Col>
      </Row>

      {filtrosMovimientos.isLoading ? (
        <Row className="mt-3">
          <Col>
            <InputPlaceholder />
          </Col>
          <Col>
            <InputPlaceholder />
          </Col>
          <Col>
            <InputPlaceholder />
          </Col>
        </Row>
      ) : (
        <Row>
          <Col>
            <FormWithValidation.LegacySelect
              name="nombreEstablecimiento"
              label="Establecimiento"
              emptyOption="Todos los establecimientos"
              options={filtrosMovimientos.data?.establecimientos}
              mapOptions={true}
            />
          </Col>
          <Col>
            <FormWithValidation.LegacySelect
              name="codigoRubro"
              label="Rubro"
              emptyOption="Todos los rubros"
              options={filtrosMovimientos.data?.rubros}
              mapOptions={true}
            />
          </Col>
          <Col>
            <FormWithValidation.LegacySelect
              name="detalleTipoOperacion"
              label="Tipo de Transacción"
              emptyOption="Todos los tipos de transacciones"
              options={filtrosMovimientos.data?.detalleTipoOperacion}
              mapOptions={true}
            />
          </Col>
        </Row>
      )}
      <div className="d-flex justify-content-end" style={{ gap: 8 }}>
        <Button
          type="button"
          variant="outline"
          color="destructive"
          onClick={() => methods.reset(EMPTY_FORM_VALUE)}
        >
          Limpiar
        </Button>
        <Button type="submit">Consultar</Button>
      </div>
    </FormWithValidation>
  );
};
